import supabase from '../supabase/supabase'

const BUCKET_NAME = 'vidroid-temp-assets'

/**
 * Verify storage bucket access
 * Changed to use .list() on the specific bucket instead of .listBuckets()
 * This avoids RLS permission issues with listing all project buckets
 */
export const initializeBucket = async () => {
  try {
    console.log('🔧 Verifying Supabase storage bucket access...')

    // Attempt to list files in the specific bucket
    // This tests if the bucket exists AND if we have permission to read it
    // We limit to 1 item to keep it lightweight
    const { data, error } = await supabase.storage
      .from(BUCKET_NAME)
      .list('temp', { limit: 1 })

    if (error) {
      console.warn('⚠️ Bucket access check warning:', error.message)
      // We don't throw here anymore. 
      // RLS might block listing but allow uploads (INSERT only policies).
      // We'll proceed optimistically.
      return false
    }

    console.log('✅ Bucket access verified:', BUCKET_NAME)
    return true
  } catch (error) {
    console.error('❌ Exception in initializeBucket:', error)
    return false
  }
}

/**
 * Test if bucket is accessible by attempting a simple operation
 */
export const testBucketAccess = async () => {
  return await initializeBucket()
}

/**
 * Upload a file to Supabase Storage with retry logic
 * @param {File} file - The file to upload (image or video)
 * @param {string} type - 'image' or 'video'
 * @returns {Promise<string>} - Public URL of the uploaded file
 */
export const uploadTempFile = async (file, type = 'image') => {
  const MAX_RETRIES = 3
  let lastError = null

  for (let attempt = 1; attempt <= MAX_RETRIES; attempt++) {
    try {
      console.log(`📤 Upload attempt ${attempt}/${MAX_RETRIES}...`)

      // Generate unique filename with timestamp
      const timestamp = Date.now()
      const randomId = Math.random().toString(36).substring(7)
      const extension = file.name.split('.').pop()
      const fileName = `${type}_${timestamp}_${randomId}.${extension}`
      const filePath = `temp/${fileName}`

      console.log('📤 Uploading file to Supabase Storage:', filePath)
      console.log('File size:', (file.size / 1024 / 1024).toFixed(2), 'MB')

      // Upload file to Supabase Storage
      // We skip the strict initializeBucket() check here and just try to upload
      const { data, error } = await supabase.storage
        .from(BUCKET_NAME)
        .upload(filePath, file, {
          cacheControl: '3600', // 1 hour cache
          upsert: false,
          contentType: file.type
        })

      if (error) {
        console.error(`❌ Upload error (attempt ${attempt}):`, error)
        lastError = error

        // If it's a specific "Bucket not found" error from the upload attempt
        if (error.message.includes('Bucket not found')) {
           throw new Error(
            `Supabase Bucket '${BUCKET_NAME}' not found. Please ensure you created it in the Dashboard and it is set to Public.`
          )
        }

        // If it's a permission error
        if (error.message.includes('violates row-level security policy')) {
          throw new Error(
            'Upload failed: Permission denied. Please check your Storage Policies in Supabase Dashboard (INSERT permission needed for public).'
          )
        }

        // Retry on network errors
        if (attempt < MAX_RETRIES) {
          console.log(`⏳ Waiting 2 seconds before retry...`)
          await new Promise(resolve => setTimeout(resolve, 2000))
          continue
        }

        throw new Error(`Upload failed: ${error.message}`)
      }

      console.log('✅ File uploaded successfully:', data)

      // Get public URL
      const { data: urlData } = supabase.storage
        .from(BUCKET_NAME)
        .getPublicUrl(filePath)

      const publicUrl = urlData.publicUrl
      console.log('🔗 Public URL generated:', publicUrl)

      // Schedule deletion after 1 hour
      scheduleFileDeletion(filePath)

      return publicUrl
    } catch (error) {
      console.error(`❌ Error in uploadTempFile (attempt ${attempt}):`, error)
      lastError = error

      if (attempt === MAX_RETRIES) {
        throw error
      }

      // Wait before retry
      console.log(`⏳ Waiting 2 seconds before retry...`)
      await new Promise(resolve => setTimeout(resolve, 2000))
    }
  }

  throw lastError || new Error('Upload failed after multiple attempts')
}

/**
 * Schedule file deletion after 1 hour
 * @param {string} filePath - Path of the file to delete
 */
const scheduleFileDeletion = (filePath) => {
  // Delete after 1 hour (3600000 ms)
  setTimeout(async () => {
    try {
      console.log('🗑️ Auto-deleting file:', filePath)

      const { error } = await supabase.storage
        .from(BUCKET_NAME)
        .remove([filePath])

      if (error) {
        console.error('❌ Error deleting file:', error)
      } else {
        console.log('✅ File deleted successfully:', filePath)
      }
    } catch (error) {
      console.error('❌ Error in scheduleFileDeletion:', error)
    }
  }, 3600000) // 1 hour
}

/**
 * Manually delete a file from Supabase Storage
 * @param {string} publicUrl - Public URL of the file
 */
export const deleteFile = async (publicUrl) => {
  try {
    // Extract file path from public URL
    const urlParts = publicUrl.split('/storage/v1/object/public/')
    if (urlParts.length < 2) {
      throw new Error('Invalid public URL format')
    }

    const pathWithBucket = urlParts[1]
    const filePath = pathWithBucket.replace(`${BUCKET_NAME}/`, '')

    console.log('🗑️ Deleting file:', filePath)

    const { error } = await supabase.storage
      .from(BUCKET_NAME)
      .remove([filePath])

    if (error) {
      console.error('❌ Error deleting file:', error)
      throw error
    }

    console.log('✅ File deleted successfully')
  } catch (error) {
    console.error('❌ Error in deleteFile:', error)
    throw error
  }
}

/**
 * Clean up old files (older than 1 hour) - can be called manually
 */
export const cleanupOldFiles = async () => {
  try {
    console.log('🧹 Starting cleanup of old files...')

    const { data: files, error: listError } = await supabase.storage
      .from(BUCKET_NAME)
      .list('temp', {
        limit: 1000,
        sortBy: { column: 'created_at', order: 'asc' }
      })

    if (listError) {
      console.error('❌ Error listing files:', listError)
      return
    }

    const oneHourAgo = Date.now() - 3600000
    const filesToDelete = files
      .filter(file => {
        const fileTime = new Date(file.created_at).getTime()
        return fileTime < oneHourAgo
      })
      .map(file => `temp/${file.name}`)

    if (filesToDelete.length === 0) {
      console.log('✅ No old files to delete')
      return
    }

    console.log(`🗑️ Deleting ${filesToDelete.length} old files...`)

    const { error: deleteError } = await supabase.storage
      .from(BUCKET_NAME)
      .remove(filesToDelete)

    if (deleteError) {
      console.error('❌ Error deleting old files:', deleteError)
    } else {
      console.log(`✅ Deleted ${filesToDelete.length} old files`)
    }
  } catch (error) {
    console.error('❌ Error in cleanupOldFiles:', error)
  }
}

/**
 * Get file size in MB
 * @param {File} file - The file to check
 * @returns {number} - Size in MB
 */
export const getFileSizeMB = (file) => {
  return (file.size / (1024 * 1024)).toFixed(2)
}

/**
 * Validate file type
 * @param {File} file - The file to validate
 * @param {string} type - 'image' or 'video'
 * @returns {boolean} - Whether the file is valid
 */
export const isValidFileType = (file, type = 'image') => {
  if (type === 'image') {
    return file.type.startsWith('image/')
  } else if (type === 'video') {
    return file.type.startsWith('video/')
  }
  return false
}